import React, { useMemo, useState, useSyncExternalStore } from "react";
import {
  applyMenuNavigationVisibility,
  MENU_ITEMS,
  pathnameToWebBottomNavTab,
  getSessionPatchSnapshot,
  getSessionPatchVersion,
  subscribeSessionPatch,
} from "@repo/utils";
import { useMenuNavigationProgram } from "../../context/MenuNavigationContext";
import {
  isWebTestsDrawerPath,
  resolveMenuDrawerVisibilityArgs,
} from "./resolveMenuDrawerVisibilityArgs";
import { VStack, HStack, Text, Box, Icon, Divider, Button } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useMenuDrawerStyles } from "./styles";
import { useTranslation } from "react-i18next";
import { Modal } from "@repo/ui";

export type MenuItems = {
  id: string;
  label: string;
  icon: string;
  visible: boolean;
  height?: number;
  width?: number;
};

export type MenuDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  menuItems?: MenuItems[];
  menuItemFooter?: React.ReactNode;
  onMenuItemPress?: (item: MenuItems) => void;
  navigate?: any;
  handleLogOut?: () => void;
};

export const MenuDrawer: React.FC<MenuDrawerProps> = ({
  onClose,
  navigate,
  handleLogOut,
}) => {
  const { theme } = useTheme();
  const styles = useMenuDrawerStyles();
  const { t } = useTranslation("common");
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const menuNavCtx = useMenuNavigationProgram();
  const sessionPatchGeneration = useSyncExternalStore(
    subscribeSessionPatch,
    getSessionPatchVersion,
    getSessionPatchVersion,
  );
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const activeBottomNavTab = pathnameToWebBottomNavTab(pathname);

  const { gradeId, examIds, verticalId } = useMemo(() => {
    const patch = getSessionPatchSnapshot();
    const patchPrograms = patch.enrolledPrograms;
    const applyOverlay =
      isWebTestsDrawerPath(pathname) &&
      Array.isArray(patchPrograms) &&
      patchPrograms.length > 0;
    return resolveMenuDrawerVisibilityArgs(menuNavCtx, patch, {
      applyPatchOverlay: applyOverlay,
    });
  }, [
    menuNavCtx.gradeId,
    menuNavCtx.verticalId,
    menuNavCtx.examIds.join(","),
    pathname,
    sessionPatchGeneration,
  ]);

  const showAppVersion = false;

  const MenuItemFooter = useMemo(() => {
    return (
      <VStack gap="xs" align="center" style={styles.drawerFooterContainer}>
        <HStack gap="lg" align="center" style={styles.drawerFooterLinksRow}>
          <a
            href="https://terms.infinitylearn.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...styles.drawerFooterText,
              cursor: "pointer",
            }}
          >
            {t("menuDrawer.termsAndConditions", {
              defaultValue: "Terms & Conditions",
            })}
          </a>
          <Text variant="caption" style={styles.drawerFooterSeparator}>
            {t("menuDrawer.separator", {
              defaultValue: "&",
            })}
          </Text>
          <a
            href="https://privacy.infinitylearn.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...styles.drawerFooterText,
              cursor: "pointer",
            }}
          >
            {t("menuDrawer.privacyPolicy", {
              defaultValue: "Privacy Policy",
            })}
          </a>
        </HStack>

        {showAppVersion && (
          <Text variant="caption" style={styles.drawerFooterVersion}>
            {t("menuDrawer.appVersion", {
              version: "1.0.40.3.251128",
              defaultValue: "App Version : {{version}}",
            })}
          </Text>
        )}
      </VStack>
    );
  }, [t]);

  const visibleMenuItems = useMemo(
    () =>
      applyMenuNavigationVisibility(
        MENU_ITEMS,
        gradeId,
        examIds,
        verticalId,
      ).filter((item) => item.visible),
    [gradeId, examIds, verticalId],
  );

  const logoutModalBody = (
    <Box style={styles.logoutModalBody}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={t("menuDrawer.close")}
        onClick={() => setIsLogoutOpen(false)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsLogoutOpen(false);
          }
        }}
        style={styles.logoutModalClose}
      >
        <Icon name="close" size={22} color={theme.action.primary.default} />
      </Box>

      <Box style={styles.logoutIconSection}>
        <Box mb={20}>
          <Icon
            name="log_out"
            width={56}
            height={56}
            color={theme.feedback.error}
          />
        </Box>

        <Text style={styles.logoutText}>
          {t("menuDrawer.sure_want_logout")}
        </Text>

        <Box
          role="button"
          tabIndex={0}
          onClick={() => {
            handleLogOut?.();
            setIsLogoutOpen(false);
            onClose();
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleLogOut?.();
              setIsLogoutOpen(false);
              onClose();
            }
          }}
          style={styles.logoutOutlineButton}
        >
          <Text style={styles.logoutOutlineButtonText}>
            {t("menuDrawer.logout")}
          </Text>
        </Box>

        <Box
          role="button"
          tabIndex={0}
          onClick={() => setIsLogoutOpen(false)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsLogoutOpen(false);
            }
          }}
        >
          <Text style={styles.logoutCloseLink}>{t("menuDrawer.close")}</Text>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box style={styles.container}>
      <Modal
        isModalOpen={isLogoutOpen}
        onClose={() => setIsLogoutOpen(false)}
        showHeader={false}
        showFooter={false}
        body={logoutModalBody}
        height={"auto"}
        width={"100vw"}
        styles={{
          overlay: styles.overlayLogout,
          modal: {
            borderTopLeftRadius: 40,
            borderTopRightRadius: 40,
            backgroundColor: theme.colors.background[50],
          },
        }}
      />
      <Box style={styles.listContainer}>
        {visibleMenuItems.map((item) => {
          const isActive =
            item.id === "live-classes"
              ? activeBottomNavTab === "play"
              : window.location.href.includes(item.url);

          return (
            <VStack key={item.id}>
              <Button
                variant="ghost"
                size="md"
                height={52}
                onPress={() => {
                  if (item.url === "/log-out") {
                    setIsLogoutOpen(true);
                  } else {
                    navigate(item.url);
                  }
                }}
                style={styles.menuButton}
              >
                <HStack gap="sm" align="center" style={styles.menuItemContent}>
                  <Icon
                    name={item.icon as any}
                    height={item.height}
                    width={item.width}
                    color={
                      isActive
                        ? theme.action.primary.default
                        : theme.icon.bottomPillInactive
                    }
                  />
                  <Text
                    variant="body"
                    style={
                      isActive
                        ? styles.menuLabelActive
                        : styles.menuLabelInactive
                    }
                  >
                    {item.label}
                  </Text>
                </HStack>
              </Button>

              {item.id === "Doubts" && <Divider style={styles.divider} />}
              {item.id === "Subscriptions" && (
                <Divider style={styles.divider} />
              )}
            </VStack>
          );
        })}
      </Box>

      {!!MenuItemFooter && MenuItemFooter}
      <Box onClick={onClose} style={styles.closeButton}>
        <Icon name="close" size={20} color={theme.text.primary} />
      </Box>
    </Box>
  );
};

MenuDrawer.displayName = "MenuDrawer";

export default MenuDrawer;
